<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <TopNavbar />
    <header class="flex justify-between items-center bg-dark-grey p-4 mb-4">
      <h1 class="text-xl font-semibold">Create Activation</h1>
      <div class="flex items-center text-sm gap-2">
        <button
          class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg hover:opacity-90"
          @click="submitForm"
        >
          Create
        </button>
        <button
          class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg hover:opacity-90"
          @click="cancelForm"
        >
          Cancel
        </button>
      </div>
    </header>

    <form
      class="flex overflow-y-auto flex-col pb-6 flex-1 space-y-6 px-4"
      @submit.prevent="submitForm"
    >
      <div class="flex flex-col gap-1">
        <label for="project" class="text-sm text-left font-medium">Project *</label>
        <CustomDropdown
          id="project"
          v-model="form.project"
          :options="projectOptions"
          placeholder="Select Project"
          :showChevron="true"
          @click="errors.project = ''"
        />
        <span v-if="errors.project" class="text-red-500 text-xs text-left pl-1">{{
          errors.project
        }}</span>
      </div>

      <div class="flex flex-col gap-1">
        <label for="agent" class="text-sm font-medium text-left">Agent *</label>
        <CustomDropdown
          id="agent"
          v-model="form.agent"
          :options="agentOptions"
          placeholder="Select Agent"
          :showChevron="true"
          @click="errors.agent = ''"
        />
        <span v-if="errors.agent" class="text-red-500 text-xs text-left pl-1">{{
          errors.agent
        }}</span>
      </div>

      <div class="flex flex-col gap-1">
        <label for="instructions" class="text-sm text-left font-medium"
          >Instructions *</label
        >
        <CustomDropdown
          id="instructions"
          v-model="form.instructions"
          :options="instructionsOptions"
          placeholder="Select Instructions"
          :showChevron="true"
          @click="errors.instructions = ''"
        />
        <span v-if="errors.instructions" class="text-red-500 pl-1 text-xs text-left">{{
          errors.instructions
        }}</span>
      </div>

      <div class="flex flex-col gap-1">
        <label for="agent-email" class="text-sm text-left font-medium"
          >Agent Email Address *</label
        >
        <CustomDropdown
          id="agent-email"
          v-model="form.agentEmail"
          :options="agentEmailOptions"
          placeholder="Select Agent Email"
          :showChevron="true"
          @click="errors.agentEmail = ''"
        />
        <span v-if="errors.agentEmail" class="text-red-500 pl-1 text-xs text-left">{{
          errors.agentEmail
        }}</span>
      </div>

      <div class="flex flex-col gap-1">
        <label for="agent-phone" class="text-sm text-left font-medium"
          >Agent Phone Number *</label
        >
        <CustomDropdown
          id="agent-phone"
          v-model="form.agentPhone"
          :options="agentPhoneOptions"
          placeholder="Select Agent Phone Number"
          :showChevron="true"
          @click="errors.agentPhone = ''"
        />
        <span v-if="errors.agentPhone" class="text-red-500 pl-1 text-xs text-left">{{
          errors.agentPhone
        }}</span>
      </div>

      <div class="flex flex-col gap-1">
        <label for="inbox-phone" class="text-sm text-left font-medium"
          >Project Inbox Phone Number *</label
        >
        <CustomDropdown
          id="inbox-phone"
          v-model="form.projectInboxPhone"
          :options="projectInboxPhoneOptions"
          placeholder="Select Project Inbox Phone Number"
          :showChevron="true"
          @click="errors.projectInboxPhone = ''"
        />
        <span
          v-if="errors.projectInboxPhone"
          class="text-red-500 pl-1 text-xs text-left"
          >{{ errors.projectInboxPhone }}</span
        >
      </div>
    </form>
    <ErrorModal
      :isOpen="showErrorModal"
      heading="An error occurred"
      :message="errorModalText"
      linkText="Contact Us"
      contactRoute="/contact"
      @close="showErrorModal = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import CustomDropdown from "@/components/CustomDropdown.vue";
import { useActivationStore } from "@/stores/activation";
import { useAgentStore } from "@/stores/agent";
import { useProjectStore } from "@/stores/project";
import { useInstructionStore } from "@/stores/instruction";
import ErrorModal from "@/components/ErrorModal.vue";

const router = useRouter();
const form = ref({
  project: "",
  agent: "",
  instructions: "",
  agentEmail: "",
  agentPhone: "",
  projectInboxPhone: "",
});

// Error state
const errors = ref({});

const projectOptions = ref([]);
const agentOptions = ref([]);
const instructionsOptions = ref([]);
const showErrorModal = ref(false);
const errorModalText = ref("");

const agentEmailOptions = ref([]);
const agentPhoneOptions = ref([]);
const projectInboxPhoneOptions = ref([]);

const { fetchAgents } = useAgentStore();
const { fetchInstructions } = useInstructionStore();
const { fetchAllProjects } = useProjectStore();

const agentStore = useAgentStore();
const instructionStore = useInstructionStore();
const projectStore = useProjectStore();
const activationStore = useActivationStore();

// Validation function
const validateForm = () => {
  errors.value = {}; // Reset errors
  let isValid = true;

  if (!form.value.project) {
    errors.value.project = "Project is required.";
    isValid = false;
  }
  if (!form.value.agent) {
    errors.value.agent = "Agent is required.";
    isValid = false;
  }
  if (!form.value.instructions) {
    errors.value.instructions = "Instructions are required.";
    isValid = false;
  }
  // if (!form.value.agentEmail) {
  //   errors.value.agentEmail = "Agent Email is required.";
  //   isValid = false;
  // }
  if (!form.value.agentPhone) {
    errors.value.agentPhone = "Agent Phone is required.";
    isValid = false;
  }
  if (!form.value.projectInboxPhone) {
    errors.value.projectInboxPhone = "Project Inbox Phone is required.";
    isValid = false;
  }

  return isValid;
};

// Submit form
const submitForm = async () => {
  if (validateForm()) {
    try {
      const activationData = {
        project_id: form.value.project,
        agent_id: form.value.agent,
        is_active: true,
        instruction_id: form.value.instructions,
        agent_email_id: form.value.agentEmail,
        agent_phone: form.value.agentPhone.toString(),
      };

      // Call your activation API here

      await useActivationStore().activateProject(activationData);

      await useActivationStore().fetchActivations();

      // Redirect or reset after successful submission
      router.push({ name: "ActivationsPage" });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  }
};
function handleFetchError(err) {
  let raw = "";
  const data = err.response?.data;

  if (typeof data?.message === "string") {
    raw = data.message;
  } else if (Array.isArray(data?.detail)) {
    // join all validation msgs into one string
    raw = data.detail.map((d) => d.msg || "").join(", ");
  } else if (typeof data?.detail === "string") {
    raw = data.detail;
  }

  const msg = raw || err.message || "";

  if (
    msg.includes("Default Agent has not been set") ||
    msg.includes("Default Instructions have not been set")
  ) {
    errorModalText.value = msg;
    showErrorModal.value = true;
  } else {
    console.error(err);
    throw err;
  }
}

// Cancel form
const cancelForm = () => {
  router.push({ name: "ActivationsPage" });
};

onMounted(async () => {
  try {
    await fetchAllProjects();
    try {
      await fetchAgents();
    } catch (err) {
      return handleFetchError(err);
    }
    try {
      await fetchInstructions();
    } catch (err) {
      return handleFetchError(err);
    }
    await activationStore.getUnassignedEmails(); // Fetch unassigned emails
    await activationStore.getUnassignedPhoneNumbers(); // Fetch unassigned phone numbers

    // Map the fetched data to options for the dropdowns
    projectOptions.value = projectStore.projects.map((project) => ({
      label: project.address_1,
      value: project.id,
    }));

    agentOptions.value = agentStore.agents.map((agent) => ({
      label: agent.agent_name,
      value: agent.id,
    }));

    instructionsOptions.value = instructionStore.instructions.map((instruction) => ({
      label: instruction.name,
      value: instruction.id,
    }));

    agentEmailOptions.value = activationStore.unassignedEmails.map((email) => ({
      label: email.email,
      value: email.id,
    }));

    agentPhoneOptions.value = activationStore.unassignedPhoneNumbers.map((phone) => ({
      label: phone.phone_number,
      value: phone.phone_number,
    }));

    projectInboxPhoneOptions.value = [
      { label: "+1-111-222-3333", value: "+1-111-222-3333" },
      { label: "+1-444-555-6666", value: "+1-444-555-6666" },
      { label: "+1-777-888-9999", value: "+1-777-888-9999" },
    ];
  } catch (error) {
    console.error("Error fetching data:", error);
  }

  // Populate form values with query params if present
  const query = router.currentRoute.value.query;
  form.value.project = query.project || "";
  form.value.agent = query.agent || "";
  form.value.instructions = query.instructions || "";
  form.value.agentEmail = query.agentEmail || "";
  form.value.agentPhone = query.agentPhone || "";
  form.value.projectInboxPhone = query.inboxPhone || "";

  await projectStore.fetchAllProjects();
  projectOptions.value = projectStore.projects.map((project) => ({
    label: project.address_1,
    value: project.id,
  }));

  await agentStore.fetchAgents();
  agentOptions.value = agentStore.agents.map((agent) => ({
    label: agent.agent_name,
    value: agent.id,
  }));

  await instructionStore.fetchInstructions();
  instructionsOptions.value = instructionStore.instructions.map((instruction) => ({
    label: instruction.name,
    value: instruction.id,
  }));

  await activationStore.getUnassignedEmails();
  if (activationStore.unassignedEmails.length === 0) {
    agentEmailOptions.value = [
      { label: "example1@example.com", value: "example1@example.com" },
      { label: "example2@example.com", value: "example2@example.com" },
      { label: "example3@example.com", value: "example3@example.com" },
      { label: "example4@example.com", value: "example4@example.com" },
    ];
  } else {
    agentEmailOptions.value = activationStore.unassignedEmails.map((email) => ({
      label: email.email,
      value: email.id,
    }));
  }

  await activationStore.getUnassignedPhoneNumbers();
  if (activationStore.unassignedPhoneNumbers.length === 0) {
    const randomPhoneNumbers = [
      { phone_number: "123-456-7890", id: "123-456-7890" },
      { phone_number: "234-567-8901", id: "234-567-8901" },
      { phone_number: "345-678-9012", id: "345-678-9012" },
      { phone_number: "456-789-0123", id: "456-789-0123" },
    ];
    agentPhoneOptions.value = randomPhoneNumbers.map((phone) => ({
      label: phone.phone_number,
      value: phone.id,
    }));
    projectInboxPhoneOptions.value = randomPhoneNumbers.map((phone) => ({
      label: phone.phone_number,
      value: phone.id,
    }));
  } else {
    agentPhoneOptions.value = activationStore.unassignedPhoneNumbers.map((phone) => ({
      label: phone.phone_number,
      value: phone.id,
    }));
    projectInboxPhoneOptions.value = activationStore.unassignedPhoneNumbers.map(
      (phone) => ({
        label: phone.phone_number,
        value: phone.id,
      })
    );
  }
});
</script>
